/* This is the Root component mainly initializes Redux and React Router. */

import React from 'react'
import PropTypes from 'prop-types'
import { Provider, useSelector } from 'react-redux'
import { Switch, Route, Redirect } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import history from './common/history'
import Login from './features/home/Login'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { appendAppVersionHeaders } from './common/appVersionHeaders'
import _ from 'lodash'
import CargandoView from './features/common/CargandoView'
import SinPermisosView from './features/common/SinPermisosView'
import { ToastContainer } from 'react-toastify'
import { ThemeProvider } from '@material-ui/core/styles'
import 'react-toastify/dist/ReactToastify.css'
import { theme } from './Root.mui-theme'
import {QueryClient, QueryClientProvider} from 'react-query'
import { App } from './features/home';
import { getToken } from '../src/common/authorization'
import DataIdAttributeAdder from './features/common/DataIdAttributeAdder'
import { SocketProvider } from './common/socketContext'
import { RefreshProvider } from './features/common/PullToRefresh/PullToRefreshContext.js'
import { StoreRedirectionModal } from './features/common/storeRedirection'

// Pantallas públicas / de uso puntual: se cargan bajo demanda (hay <Suspense> en RenderRouteConfigV4).
// Antes eran imports estáticos y arrastraban al bundle inicial draft-js, react-beautiful-dnd,
// react-bootstrap-table, moment-timezone, etc.
const Welcome = React.lazy(() => import('./features/home/Welcome'))
const RestablecerContrasenia = React.lazy(() => import('./features/home/restablecer-contrasenia/MainRestablecerContrasenia'))
const MainRecuperarContrasenia = React.lazy(() => import('./features/home/recuperar-contrasenia/MainRecuperarContrasenia'))
const GenerarPdf = React.lazy(() => import('./features/common/GenerarPdf'))
const VerificationPdfPreview = React.lazy(() => import('./features/peritador/VerificationPdfPreview'))
const ConfirmUserAccount = React.lazy(() => import('usuarios/ConfirmUserAccount'))
const Presupuesto = React.lazy(() => import('./features/presupuesto/Presupuesto'))
const PublicCatalog = React.lazy(() => import('./features/catalogo-publico/PublicCatalog'))

// react-query global configuration
const AXIOS_TIMEOUT = process.env.REACT_APP_TIMEOUT_REQUEST || 60000
let isAppVersionInterceptorRegistered = false
const refetchOnWindowFocus = process.env.REACT_APP_REFETCH_ON_WINDOWS_FOCUS !== 'false'
const staleTime = process.env.REACT_APP_STALE_TIME || 1000 * 60
const cacheTime = process.env.REACT_APP_CACHE_TIME || 1000 * 60 * 10
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus,
      staleTime,
      cacheTime
    }
  }
})

const withoutPermissions = [
  '/error',
  '/inicio',
  '/usuarios/editar-usuario',
  '/',
  '/*'
]

const hasPermiso = (permisos, route) => {
  const modulo = route.split('/')[1]
  return permisos.some(nav => {
    // Verifico primero si se encuentra el modulo en Nav
    if(nav?.slug === modulo) {
      return true;
    } else if(nav?.content) {  // Si no, si el Nav tiene NavItems, verifico en dicho array
      return nav?.content.some(navItem => navItem.slug === modulo)
    } else {
      return false
    }

  }) || withoutPermissions.includes(route)
}

const RenderRouteApp = ({routeConfig: routeConfigProp}) => {
  const [ routesList, setRoutesList ] = React.useState([])
  const permisos = useSelector(state => state.common.user.system) || []

  const getRouteComponent = (route, contextPath, routesArray=[]) => {
    let newContextPath
    if (/^\//.test(route.path)) {
      newContextPath = route.path
    } else {
      newContextPath = `${contextPath}/${route.path}`
    }
    newContextPath = newContextPath.replace(/\/+/g, '/')

    if (route?.childRoutes?.length) {
      route.childRoutes.forEach(r => getRouteComponent(r, newContextPath, routesArray))
    } else if (route.component) {
    
      routesArray.push(<Route
        key = {newContextPath}
        exact path = {newContextPath}
        render = {(renderProps) => {
          const hasPermission = hasPermiso(permisos, newContextPath);
          return hasPermission ? <route.component {...renderProps} ></route.component> : <SinPermisosView />
        }}
      />,)
    }
  }

  React.useEffect(()=>{
    let finalRoutes = []
    routeConfigProp[0].childRoutes.forEach(route => getRouteComponent(route, '/', finalRoutes));
    setRoutesList(finalRoutes)
  }, [])

  return (
    <React.Suspense fallback={<CargandoView />}>
      <Switch>
        <Route
          exact path='/'
          render={routeProps => <Redirect to={'/inicio'} state= {{ from: routeProps.location }} />}
        />
        {routesList}
      </Switch>
    </React.Suspense>
  )
}

const RenderRouteConfigV4 = ({routeConfig}) => {
  return (
    <React.Suspense fallback={<CargandoView />}>
      <Switch>
        <Route exact path="/login" component={Login} isIndex={true} />
        <Route exact path="/restablecer-contrasena" component={RestablecerContrasenia} isIndex={true} />
        <Route exact path="/recuperar-contrasena/:id" component={MainRecuperarContrasenia} isIndex={true} />
        <Route exact path="/logout" render={ props => <Login logout {...props} />}  />
        <Route exact path="/bienvenido/:id" component={Welcome} isIndex={true} />
        <Route exact path="/usuarios/confirmar/:id" component={ConfirmUserAccount} isIndex={true} />
        <Route exact path="/generar-pdf/:id" component={GenerarPdf} />
        <Route exact path="/peritador/verification-pdf/:requestId/:verificationStatusId" component={VerificationPdfPreview} />
        <Route path="/presupuesto-interactivo" component={Presupuesto} />
        <Route path="/catalogo-publico" component={PublicCatalog} />
        {/* App render all modules routes */}
        <Route path='/' render={props => {
            if(!getToken()){
              return <Redirect to={{pathname:'/login',state:{from: props.location }}} />
            }
            return  <App {...props}>  <RenderRouteApp routeConfig={routeConfig} /> </App>}
          }
        />
      </Switch>
    </React.Suspense>
  )
}
const Root = ({store, routeConfig}) => {
  axios.defaults.timeout = AXIOS_TIMEOUT
  if (!isAppVersionInterceptorRegistered) {
    isAppVersionInterceptorRegistered = true
    axios.interceptors.request.use(
      request => {
        const isMobileSystem = store.getState()?.common?.user?.isMobileSystem || Capacitor.isNativePlatform()
        return appendAppVersionHeaders(request, isMobileSystem)
      },
      error => Promise.reject(error)
    )
  }
  axios.interceptors.response.use(
    response => response,
    error => {
      if (error.code === 'ECONNABORTED') {
        error.message = 'El servidor no responde'
      }
      const notLogin = window.location.pathname !== "/login"
      if (window.location.pathname !== '/presupuesto' && window.location.pathname !== '/catalogo-publico') {
        if(error?.response?.status === 401 && notLogin) {
          localStorage.removeItem('token')
          localStorage.removeItem('asofix-redux-store')
          localStorage.removeItem('asofix-redux-store-timestamp')
          window.location.href = '/logout'
        }
      }
      return Promise.reject(error)
    },
  )

  const saveStore = _.throttle(() => {
    const currentState = store.getState()
    // No guardar el modal de llamada como abierto
    const storeState = currentState?.leads?.whatsapp?.show_calling_modal
      ? {
          ...currentState,
          leads: {
            ...currentState.leads,
            whatsapp: {
              ...currentState.leads.whatsapp,
              show_calling_modal: false,
            },
          },
        }
      : currentState
    try {
      localStorage.setItem('asofix-redux-store', JSON.stringify(storeState))
      localStorage.setItem(
        'asofix-redux-store-timestamp',
        JSON.stringify(Date.now()),
      )
    } catch (error) {
      // try catch por si el local storage esta lleno que no se corte la ejecucion
      console.log(error)
    }
  }, 1000)

  React.useEffect(() => {
    store.subscribe(saveStore)
    return () => saveStore.flush()
  }, []);


  return (
    <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <SocketProvider>
            <RefreshProvider>
              <ThemeProvider theme={theme}>
                <ConnectedRouter history={history}>
                  <DataIdAttributeAdder />
                  <RenderRouteConfigV4 routeConfig={routeConfig} />
                </ConnectedRouter>
                <ToastContainer style={{ zIndex: 1000001 }} autoClose={2000} />
                <StoreRedirectionModal />
              </ThemeProvider>
            </RefreshProvider>
          </SocketProvider>
        </QueryClientProvider>
    </Provider>
  )
}

Root.prototype = {
  store: PropTypes.object.isRequired,
  routeConfig: PropTypes.array.isRequired,
}

export default Root;